import { computed } from 'vue'
import { useBudgetData } from './useBudgetData'

const TREND_WINDOW_DAYS = 7;

export function useSavingsCalculations() {
  const { 
    currentMonthTransactions, 
    dailyBudget, 
    currentDate, 
    daysInMonth, 
    isViewingCurrentMonth, 
    actualCurrentDate, 
    budgetStartDate, 
    isPartialMonth 
  } = useBudgetData()

  // Calculate daily savings data for the current month
  const dailySavingsData = computed(() => {
    const now = currentDate.value;
    
    // Determine first day to show based on whether it's a partial month
    let firstDayToShow = 1;
    if (isPartialMonth.value && budgetStartDate.value) {
      const startDate = new Date(budgetStartDate.value);
      firstDayToShow = startDate.getDate();
    }
    
    // For current month, only show up to today
    // For past months, show all days
    const lastDayToShow = isViewingCurrentMonth.value 
      ? actualCurrentDate.value.getDate() 
      : daysInMonth.value;

    // Group transactions by day for better performance (O(n) complexity)
    const transactionsByDay = new Map();
    currentMonthTransactions.value.forEach(t => {
      const day = new Date(t.date).getDate();
      if (!transactionsByDay.has(day)) {
        transactionsByDay.set(day, []);
      }
      transactionsByDay.get(day).push(t);
    });

    const dailySavings = [];
    const runningSavings = [];
    let cumulativeTotal = 0;

    // Calculate savings for each day
    for (let day = firstDayToShow; day <= lastDayToShow; day++) {
      const dayTransactions = transactionsByDay.get(day) || [];

      // Single pass through transactions to sum by type
      let dayDebits = 0;
      let dayCredits = 0;
      
      dayTransactions.forEach(t => {
        if (t.type === 'debit') dayDebits += t.amount;
        else if (t.type === 'credit') dayCredits += t.amount;
        // Savings transactions are ignored (neutral money movement)
      });

      // Calculate net spending and daily savings
      const dayNetSpending = dayDebits - dayCredits;
      const dailySaved = Math.round((dailyBudget.value - dayNetSpending) * 100) / 100;

      dailySavings.push(dailySaved);
      cumulativeTotal += dailySaved;
      runningSavings.push(Math.round(cumulativeTotal * 100) / 100);
    }

    // Calculate average for projections
    const averageDailySaved = dailySavings.length > 0
      ? dailySavings.reduce((sum, saved) => sum + saved, 0) / dailySavings.length
      : 0;

    // Create projections for remaining days (current month only)
    const projectedSavings = [];
    if (isViewingCurrentMonth.value && lastDayToShow < daysInMonth.value) {
      let projectionTotal = cumulativeTotal;
      for (let day = lastDayToShow + 1; day <= daysInMonth.value; day++) {
        projectionTotal += averageDailySaved;
        projectedSavings.push(Math.round(projectionTotal * 100) / 100);
      }
    }

    return {
      dailySavings,
      runningSavings,
      projectedSavings,
      averageDailySaved: Math.round(averageDailySaved * 100) / 100,
      labels: Array.from({ length: lastDayToShow - firstDayToShow + 1 }, (_, i) => firstDayToShow + i),
      projectionLabels: projectedSavings.length > 0
        ? Array.from({ length: projectedSavings.length }, (_, i) => lastDayToShow + 1 + i)
        : [],
      // Combined labels for charts that need all days
      allLabels: Array.from({ length: daysInMonth.value - firstDayToShow + 1 }, (_, i) => firstDayToShow + i)
    };
  })

  // Total saved this month (cumulative)
  const totalSavedThisMonth = computed(() => {
    const { runningSavings } = dailySavingsData.value;
    return runningSavings.length > 0 ? runningSavings[runningSavings.length - 1] : 0;
  })

  // Average daily savings
  const averageDailySaved = computed(() => dailySavingsData.value.averageDailySaved)

  // Projected end-of-month savings
  const projectedEndMonthSavings = computed(() => {
    const { projectedSavings } = dailySavingsData.value;
    return projectedSavings.length > 0 ? projectedSavings[projectedSavings.length - 1] : 0;
  })

  // Calculate average savings position over the last 7 days (excluding today)
  const last7DaysAverageCumulative = computed(() => {
    const { runningSavings } = dailySavingsData.value;
    
    // Need at least 2 days (today + 1 previous day) to calculate trend
    if (runningSavings.length < 2) return 0;
    
    // Exclude today (last element) and get up to 7 previous days
    const previousDays = runningSavings.slice(0, -1);
    const daysToAverage = Math.min(TREND_WINDOW_DAYS, previousDays.length);
    const last7Days = previousDays.slice(-daysToAverage);
    const sum = last7Days.reduce((acc, val) => acc + val, 0);
    
    return Math.round((sum / daysToAverage) * 100) / 100;
  })

  // Calculate savings trend: current position vs 7-day average position
  const savingsTrend = computed(() => {
    const currentPosition = totalSavedThisMonth.value;
    const averagePosition = last7DaysAverageCumulative.value;
    
    // If no historical average, return neutral trend
    if (averagePosition === 0) {
      return {
        currentPosition: Math.round(currentPosition * 100) / 100,
        averagePosition: 0,
        difference: 0,
        percentageChange: 0,
        isPositive: currentPosition >= 0,
        isNeutral: true
      };
    }
    
    // Calculate difference and percentage change
    const difference = currentPosition - averagePosition;
    let percentageChange = (difference / Math.abs(averagePosition)) * 100;
    
    // Cap percentage at ±999% to avoid meaningless large numbers
    // When average is very small, percentage becomes unreliable
    const MAX_PERCENTAGE = 999;
    if (Math.abs(percentageChange) > MAX_PERCENTAGE) {
      percentageChange = percentageChange > 0 ? MAX_PERCENTAGE : -MAX_PERCENTAGE;
    }
    
    return {
      currentPosition: Math.round(currentPosition * 100) / 100,
      averagePosition: Math.round(averagePosition * 100) / 100,
      difference: Math.round(difference * 100) / 100,
      percentageChange: Math.round(percentageChange * 100) / 100,
      isPositive: difference >= 0,
      isNeutral: Math.abs(percentageChange) < 1 // Less than 1% change is neutral
    };
  })

  return {
    dailySavingsData,
    totalSavedThisMonth,
    averageDailySaved,
    projectedEndMonthSavings,
    last7DaysAverageCumulative,
    savingsTrend
  }
}